import { projectsDataStore } from "Stores/ProjectsStore";
import { API } from "General/API";
import { action, observable, runInAction, computed, IObservableArray, makeObservable } from "mobx";
import { CoreStore, handleStoreState } from "Stores/RESTStore/CoreStore";
import { API_URL } from "General/APIEndpoints";
import { defaultSort } from "utils/defaultSort";
import { API_MAILREQUEST_IMPORTANCE } from "General/APIEnums";
import { download } from "utils/download";

const importanceValueMap: { [key in API_MAILREQUEST_IMPORTANCE]: number } = {
  LOW: 1,
  NORMAL: 2,
  HIGH: 3,
};

function importanceSort(a: API_MailResponse, b: API_MailResponse, order: "ASC" | "DESC" = "ASC") {
  const value = order === "ASC" ? -1 : 1;
  if (importanceValueMap[a.importance] < importanceValueMap[b.importance]) {
    return value;
  }
  if (importanceValueMap[a.importance] > importanceValueMap[b.importance]) {
    return -value;
  }
  return 0;
}

export class EmailSearchStore extends CoreStore {
  data: API_MailResponse[] = [];
  total: number = 0;
  searchTextValue: string = "";
  currentPage: number = 1;
  perPage: number = 30;
  sortOrder: API_MAILPAGINATION_SORTORDER = "DESC";
  sortBy: API_MAILPAGINATION_SORTBY = "DATE";
  actionState: StoreState = "RAW";
  emailType: "search";

  constructor() {
    super();

    makeObservable(this, {
      data: observable,
      total: observable,
      searchTextValue: observable,
      currentPage: observable,
      perPage: observable,
      sortOrder: observable,
      sortBy: observable,
      actionState: observable,
      clear: action.bound,
      sortList: action.bound,
      setSearchTextValue: action.bound,
      currentPageList: computed,
      submitSearch: action.bound,
      moveMailToProject: action.bound,
      downloadPdf: action.bound,
    });

    // Initialize observable properties after makeObservable
    this.data = [] as IObservableArray<API_MailResponse>;
    this.searchTextValue = "";
    this.currentPage = 1;
    this.perPage = 30;
    this.sortOrder = "DESC";
    this.sortBy = "DATE";
    this.actionState = "RAW";
  }

  url(): string {
    return API_URL.MAILS.search_1(projectsDataStore.projectId);
  }

  clear() {
    this.data = [] as IObservableArray<API_MailResponse>;
    this.searchTextValue = "";
    this.total = 0;
  }

  @handleStoreState()
  async submitSearch(formData: API_MailSearchRequest) {
    try {
      this.searchTextValue = formData.searchText || "";
      const response: API_MultiMailResponse = await API.newData(this.url(), formData);
      runInAction(() => {
        this.data = response.items.sort((a, b) =>
          defaultSort<API_MailResponse>(a, b, "timestamp", this.sortOrder)
        ) as IObservableArray<API_MailResponse>;
        this.total = response.total;
      });
    } catch (err) {
      throw err;
    }
  }

  sortList(sortBy: API_MAILPAGINATION_SORTBY) {
    if (sortBy === this.sortBy) {
      if (this.sortOrder === "ASC") {
        this.sortOrder = "DESC";
      } else {
        this.sortOrder = "ASC";
      }
    } else {
      this.sortBy = sortBy;
      this.sortOrder = "DESC";
    }
    const sortMap: { [key in API_MAILPAGINATION_SORTBY]: keyof API_MailResponse } = {
      SUBJECT: "subject",
      ATTACHMENT: "hasAttachments",
      DATE: "timestamp",
      NAME: "from",
      IMPORTANCE: "importance",
    };
    let sortedList: API_MailResponse[];
    if (sortBy === "IMPORTANCE") {
      // tslint:disable-next-line: max-line-length
      sortedList = this.data.slice().sort((a, b) => importanceSort(a, b, this.sortOrder));
    } else {
      // tslint:disable-next-line: max-line-length
      sortedList = this.data
        .slice()
        .sort((a, b) => defaultSort<API_MailResponse>(a, b, sortMap[this.sortBy], this.sortOrder));
    }
    this.data = sortedList;
  }

  getSlice(fromIndex: number, sliceSize: number) {
    const toIndex = fromIndex + sliceSize;
    return this.data.slice(fromIndex, toIndex) as API_MailResponse[];
  }

  getListByPage(page: number, sliceSize: number) {
    const fromIndex = page < 2 ? 0 : (page - 1) * sliceSize;
    return this.getSlice(fromIndex, sliceSize);
  }

  setSearchTextValue(value = "") {
    this.searchTextValue = value;
  }

  @handleStoreState<EmailSearchStore>("actionState")
  async moveMailToProject(emailData: { [id: string]: { name: string; projectSpecific: boolean } }, folderId: number) {
    try {
      const mailTypes = Object.keys(emailData).map((mailId) => {
        const email = this.data.find((item) => item.id === mailId);
        if (email) {
          return email.type;
        } else {
          return null;
        }
      });
      const formBody: API_MailMoveRequest = {
        folderId: folderId,
        mailMoveInfo: Object.keys(emailData).map((mailId, idx) => ({
          mailId: mailId,
          mailFileName: emailData[mailId].name,
          mailProjectSpecific: !!emailData[mailId].projectSpecific,
          mailType: mailTypes[idx],
        })),
      };
      await API.editData(API_URL.MAILS.move(projectsDataStore.projectId), formBody);
      runInAction(() => {
        for (const id of Object.keys(emailData)) {
          this.data = this.data.filter((item) => item.id !== id);
        }
        this.total = this.total - 1;
      });
    } catch (err) {
      throw err;
    }
  }

  @handleStoreState<EmailSearchStore>("actionState")
  async downloadPdf(emailIds: string[]) {
    try {
      const emailsData = emailIds.map((emailId) => {
        return this.data.find((item) => item.id === emailId);
      });
      const formBody: API_MailPdfRequest[] = emailIds.map((item, idx) => ({
        projectSpecific: emailsData[idx]?.projectSpecific,
        id: item,
        mailType: emailsData[idx].type,
      }));
      const file: File = await API.getBlobByPost(API_URL.MAILS.getPdf(projectsDataStore.projectId), formBody);
      download(file, file.name, file.type);
    } catch (err) {
      throw err;
    }
  }

  async getDefaultFilenames(mailIds: string[]) {
    try {
      const formData: API_MailsFilenameRequest = {
        mailIds,
      };
      return await API.newData<API_MailIdAndFilename[]>(
        API_URL.MAILS.getFilenamesForMails(projectsDataStore.projectId),
        formData
      );
    } catch (error) {
      throw error;
    }
  }

  get currentPageList() {
    return this.getListByPage(this.currentPage, this.perPage).slice();
  }
}

export const emailSearchStore = new EmailSearchStore();
